import * as net from 'node:net';
import WebSocket from 'ws';
import { isVerbose } from '../utils.js';
import { parseErrorBody, V2ApiError } from './v2-errors.js';
import { readCredentials } from '../credentials.js';
import { getActiveOrgSlug, notSignedInMessage } from '../active-org.js';
import { loadConfig } from '../config.js';
import { getCliVersion } from '../cli-version.js';
import type { CallerKind } from '../../../shared/src/telemetry/cli-telemetry-v2.js';

/**
 * Compact grid shape returned by {@link IV2Client.getOrgs}.
 *
 * Wraps `GET /api/v2/grids` (see `packages/api/src/services/orgs-v2.ts`
 * `listOrgsForUser`). The server-side response carries more fields
 * (`description`, `org_type`, `role`, `member_count`, `last_active_at`)
 * — they're intentionally dropped here. Two reasons:
 *
 *   1. **Roles are server-authoritative for authorization.** Caching `role`
 *      locally invites stale-role bugs. Authorization gates live in
 *      server-side route middleware; the CLI never gates on cached role.
 *   2. **The CLI's only use of `/grids` is identity resolution** —
 *      "what's my active grid's UUID?" — for which `slug → grid_id → name`
 *      is sufficient.
 *
 * Decision 045: the wire URL migrated `/orgs`→`/grids` and the wire field
 * `org_id`→`grid_id` (the API dual-emits both during the transition). The
 * in-memory field name stays `org_id` because it is the identity compared
 * against the persisted `.cloudgrid/link.json` `org_id` — that link identity
 * field is a separate concern, deliberately out of scope for this migration.
 */
export interface Grid {
  org_id: string;
  slug: string;
  name: string;
}

/**
 * Pull a `warnings` array out of a v2 response body. Drops anything that
 * doesn't match the `{ code, message }` shape so a malformed entry can't
 * sneak past the type. Returns `undefined` when no usable warnings are
 * present so spreading into PlugResult adds no key.
 */
function readWarnings(body: Record<string, unknown>): { warnings?: PlugWarning[] } {
  const raw = body.warnings;
  if (!Array.isArray(raw)) return {};
  const out: PlugWarning[] = [];
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const code = (item as { code?: unknown }).code;
      const message = (item as { message?: unknown }).message;
      if (typeof code === 'string' && typeof message === 'string') {
        out.push({ code, message });
      }
    }
  }
  return out.length > 0 ? { warnings: out } : {};
}

/** Default per-request timeout — the ceiling before the client gives up on a
 *  slow/degraded API. Overridable per-invocation via `CLOUDGRID_API_TIMEOUT_MS`. */
export const DEFAULT_API_TIMEOUT_MS = 15_000;

/**
 * Resolve the per-request timeout in milliseconds. `CLOUDGRID_API_TIMEOUT_MS`
 * wins when it parses to a positive finite integer; anything else (unset, `0`,
 * negative, non-numeric) falls back to {@link DEFAULT_API_TIMEOUT_MS}. There is
 * deliberately no "disable" value — a missing timeout is the P0 hang this
 * guards against, so an operator who wants headroom sets a large number rather
 * than removing the ceiling.
 */
export function resolveApiTimeoutMs(env: NodeJS.ProcessEnv = process.env): number {
  const raw = env.CLOUDGRID_API_TIMEOUT_MS;
  if (typeof raw === 'string' && raw.trim().length > 0) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  return DEFAULT_API_TIMEOUT_MS;
}

/**
 * Build the V2ApiError thrown when a request trips its timeout. Status 0 (no
 * HTTP response arrived) and code `API_TIMEOUT` so it flows through the same
 * `handleV2Error` / `--json` envelope path as any server error — a machine
 * consumer still gets `{ error: { code: 'API_TIMEOUT', ... } }`. §23b voice:
 * plain, blameless, actionable.
 */
function apiTimeoutError(ms: number): V2ApiError {
  const window = ms >= 1000 ? `${Math.round(ms / 1000)}s` : `${ms}ms`;
  return new V2ApiError(
    0,
    'API_TIMEOUT',
    `CloudGrid didn't respond within ${window}.\n` +
      `  The API may be slow or unreachable — try again shortly.\n` +
      `  Set CLOUDGRID_API_TIMEOUT_MS to adjust the timeout.`,
  );
}

export interface V2ClientOpts {
  apiUrl: string;
  jwt: string;
  orgSlug: string | null;
  /**
   * When set, sent as `X-CloudGrid-Yaml-Org` on every request. The server's
   * org-pin middleware verifies this matches the resolved active org's slug
   * and rejects the request with 403 ORG_PINNED if not. The client-side pin
   * check in `resolveRuntimeArg` was removed under Decision 012; this field
   * and the matching header are retained as dormant defense-in-depth pending
   * Module 6's v2 endpoint cleanup.
   */
  yamlOrg?: string | null;
  userAgent?: string;
  /**
   * Request→response budget for the deploy upload POSTs (`plug` and
   * `postMultipart`) in milliseconds. The build itself is async (the server
   * returns 202 + a poll_url), so this bounds only the upload leg — a stalled
   * server surfaces a clear error instead of hanging forever (the "getting
   * stuck" bug). Defaults to `CLOUDGRID_PLUG_UPLOAD_TIMEOUT_MS` or 120s.
   */
  uploadTimeoutMs?: number;
}

/**
 * Result of `IV2Client.plug()`. Discriminated union mirrors the wire contract
 * for `POST /api/v2/entities/:id/plug`:
 *
 * - `'queued'` → 202 Accepted: a fresh deploy was enqueued. `trace_id` /
 *   `poll_url` point to the new deploy event.
 * - `'attached'` → 409 Conflict: a deploy is already in flight for this
 *   entity. The CLI should attach to the existing trace rather than fail.
 *   The 409 envelope is NOT the standard `{ error: ... }` shape, so we
 *   surface it as a successful return rather than a thrown V2ApiError.
 *
 * Mirrors the legacy v1 build response in `legacy-client.ts:42-43` so verb
 * code can share polling logic between legacy and v2 paths during the
 * v0.7.7 → v0.8 transition (audit B.1.1, "preserve" disposition).
 */
/**
 * Non-fatal warning surfaced alongside a successful plug response. Same
 * `{ code, message }` shape as v2 errors so the CLI can render warnings
 * and errors with one helper. Examples: `USER_DOCKERFILE_IGNORED` (the
 * platform stripped a user-supplied Dockerfile per decision 013).
 */
export interface PlugWarning {
  code: string;
  message: string;
}

export type PlugResult =
  | {
      kind: 'queued';
      trace_id: string;
      deploy_event_id: string;
      entity_id: string;
      entity: { id: string; name: string; namespace: string };
      poll_url: string;
      /**
       * BL567 — present when the deploy was enqueued on the task substrate
       * (`QUEUED_PLUG_ENABLED` + the entity/org allowlisted). The CLI polls
       * `GET /api/v2/tasks/<task_id>` for durable execution state instead of
       * (or in addition to) the `/deploys/<trace_id>` trace. Absent for the
       * in-process path, which still returns a `trace_id` only.
       */
      task_id?: string;
      /** BL567 — `true` when the server took the queued path. */
      queued?: boolean;
      /**
       * BL659 — the server's canonical serving URL for this entity, composed
       * once server-side by `composeEntityServingUrl` (`entityUrl()` + the
       * system-org apex special case, #1438). This is the ONE authoritative host
       * per Decision 007 — surfaces that emit a serving URL must consume it rather
       * than recompose their own. The deploy trace's `result.url` is the raw
       * `entityUrl()` primitive, which can miss the system-apex special case.
       * Absent only when the server hit a non-fatal composition hiccup.
       */
      url?: string;
      warnings?: PlugWarning[];
    }
  | {
      kind: 'attached';
      trace_id: string | null;
      status: string;
      poll_url: string | null;
      message?: string;
      /** BL659 — server's canonical serving URL; see the queued variant. */
      url?: string;
      warnings?: PlugWarning[];
    };

export interface PlugOptions {
  /** When set, sent as the `X-CloudGrid-CLI-Version` header. Optional. */
  cliVersion?: string;
}

/**
 * Caller-provided fields for {@link IV2Client.submitCliEvent}. The client
 * augments each event with `cli_version`, `os`, `node_version`, and `ts`
 * before submission so callers don't restate the same boilerplate at every
 * site. Identity (`user_id`) is JWT-resolved server-side.
 *
 * Decision 016 fixes the field set; new fields here must round-trip through
 * `validateCliEventBody` on the server. Unknown fields are silently dropped
 * by the server (forward-compat).
 */
export type CliEventType = 'error' | 'success' | 'feedback';

export interface SubmitCliEventInput {
  event_type: CliEventType;
  command: string;
  /**
   * Modernization (org → grid): callers may pass the grid slug directly. When
   * omitted the client falls back to its bound org/grid slug. Emitted on the
   * wire as BOTH `grid_slug` (canonical, CLI Telemetry v2) and the legacy
   * `org_slug` (same value) so a pre-v2 server still records grid attribution.
   */
  grid_slug?: string | null;
  /** @deprecated alias for {@link grid_slug}; kept so existing callers compile. */
  org_slug?: string | null;
  /** Grid UUID, when a caller happens to know it (usually unset — the server
   *  resolves the grid from the JWT). */
  grid_id?: string | null;
  /** The app/entity operated on (slug, `grid/slug`, or id). Emitted as
   *  `entity_ref` (v2) with the legacy `entity_id` alias for back-compat. */
  entity_id?: string | null;
  error_code?: string | null;
  error_status?: number | null;
  /** Scrubbed, human-readable error message (CLI Telemetry v2 — sent on error
   *  events in addition to `error_code` / `error_status`). */
  error_message?: string | null;
  request_id?: string | null;
  text?: string | null;
  log_excerpt?: string | null;
  /** Command wall-clock duration. Emitted as `duration_ms` (v2) with the legacy
   *  `elapsed_ms` alias for back-compat. */
  elapsed_ms?: number | null;
  outcome?: 'ok' | 'error' | null;
  /**
   * Structured flags/options as key→value (CLI Telemetry v2). Flag NAMES are
   * kept; VALUES are credential-scrubbed by the collector (`parseArgs`) and
   * again server-side. Bounded to ≤32 keys.
   */
  args?: Record<string, string> | null;
  /**
   * Structured, privacy-safe payload for events whose shape the flat fields
   * can't hold — e.g. the `FRAMEWORK_DETECT_UNKNOWN` detector signature
   * (backlog 462). The server validates this is a bounded JSON object and
   * persists it verbatim; callers MUST only place non-PII identifiers here
   * (file/dir names, dependency keys), never file contents or secrets.
   */
  metadata?: Record<string, unknown> | null;

  // ── origin (CLI Telemetry v2 — where/how the CLI was invoked) ──
  caller_kind?: CallerKind | null;
  client_name?: string | null;
  headless?: boolean | null;
  invocation_signals?: Record<string, string> | null;

  // ── intent (what the user was trying to create, when inferable) ──
  entity_type?: string | null;
  framework?: string | null;
  description?: string | null;

  // ── machine / env (beyond the always-attached os / versions) ──
  arch?: string | null;
  os_version?: string | null;
  cwd_hint?: string | null;

  /**
   * CLI-session correlation id. Reuses an agent-set `CLOUDGRID_SESSION_ID` or a
   * CLI-minted per-process id (see `recordCliEvent`). Sent as the `session_id`
   * body field (read by the v2 server) AND, for continuity, the
   * `x-cloudgrid-session` request header.
   */
  session_id?: string | null;
  /**
   * Per-command correlation id (agent-set `CLOUDGRID_TRACE_ID`). Sent as the
   * `trace_id` body field AND the `x-cloudgrid-trace` request header.
   */
  trace_id?: string | null;
}

/**
 * Public surface of the v2 API client. Both `V2Client` (the real implementation)
 * and `MockV2Client` (the test fixture in `__tests__/helpers/mock-api-client.ts`)
 * implement this interface. Verb code that wants to be testable against the mock
 * MUST declare its parameter as `IV2Client`, not `V2Client` — TypeScript's nominal
 * private-field check rejects assignment of MockV2Client to V2Client.
 */
export interface IV2Client {
  getOrgSlug(): string | null;
  /**
   * Return a client identical to this one but scoped to a different org
   * slug — i.e. one that sends `x-cloudgrid-org: <orgSlug>` on every
   * request. Backlog 320: `grid plug --org <slug>` resolves the
   * effective org *once* at the top of the verb and rebinds the client to
   * that slug so the deploy submission (and every downstream request in the
   * invocation) carries the resolved org — not the persisted active-org
   * slug the client was first constructed with. Returns `this` unchanged
   * when the slug already matches, so the no-op case allocates nothing.
   */
  withOrgSlug(orgSlug: string | null): IV2Client;
  getBaseUrl(): string;
  get<T>(path: string, qs?: Record<string, string | number | undefined>): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
  postMultipart<T>(path: string, form: FormData): Promise<T>;
  /**
   * GET /api/v2/grids — grids accessible to the authenticated user.
   *
   * The server (`orgsRouter` in `packages/api/src/routes/v2/orgs.ts`, dual-mounted
   * at `/orgs`+`/grids`) bypasses `orgContextMiddleware` for this route, so the
   * call works with or without an `x-cloudgrid-grid` header — i.e. before an
   * active grid is known. This is what makes server-authoritative
   * `getActiveOrg()` resolution possible.
   */
  getOrgs(): Promise<Grid[]>;
  /**
   * POST /api/v2/entities/:id/plug — multipart deploy submission.
   * Returns a discriminated union so callers can attach to an in-flight
   * deploy (409) without losing the existing `trace_id`.
   */
  plug(entityId: string, form: FormData, opts?: PlugOptions): Promise<PlugResult>;
  streamSSE(
    path: string,
    qs?: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ): AsyncGenerator<import('../sse-parser.js').SSEEvent>;
  /**
   * Open a local TCP-over-WebSocket tunnel to a whitelisted dev shared
   * service (mongodb / redis / n8n). The CLI first runs a single probe
   * WebSocket to the API to surface auth + connectivity errors with the
   * §23 wording the dev runner expects (401 / 403 / network). On probe
   * success, opens a local TCP server on `127.0.0.1:<localPort>` that
   * bridges every accepted connection to a fresh `/tunnel/<service>`
   * WebSocket. Returned `net.Server` is the caller's lifecycle handle —
   * call `.close()` to drain.
   *
   * The relay (and its membership gate) is the platform's purpose-built
   * replacement for `kubectl port-forward`; per decision 015 §"Tunnel
   * mechanism" the dev path runs through this method exclusively.
   *
   * `entityId` is the linked entity id from `.cloudgrid/link.json`. It is sent
   * as `?entity=<id>` on every tunnel upgrade so the API can scope the access
   * check to the entity's grid (CLI mega-plan Phase 1 — cross-tenant read fix).
   * Required: the server rejects a tunnel upgrade without it.
   */
  openTunnel(service: string, localPort: number, entityId: string): Promise<net.Server>;
  /**
   * POST `/api/v2/telemetry/cli-events` — fire-and-forget developer
   * feedback per decision 016. Augments the event with `cli_version`,
   * `os`, `node_version`, and `ts` before sending; resolves silently
   * on any error (network, 4xx, 5xx, timeout). Capped at 250ms so the
   * caller can `await` immediately before `process.exit` without
   * elastic blocking.
   */
  submitCliEvent(input: SubmitCliEventInput): Promise<void>;
  /**
   * POST `/api/v2/grids/:slug/dev-mongo-creds` — fetch the dev MONGODB_URL the
   * tunnel should use (BL449). Pre-cutover the server returns the no-auth
   * template (`auth_required: false`); post-cutover it mints/returns a per-org
   * credentialed URL (`auth_required: true`). `entityId` is the linked
   * entity's id — the server resolves its server-owned db name from it and
   * scopes the per-org user's grant accordingly. The returned `url` is a
   * `mongodb://` template the dev runner rehosts to `localhost`.
   */
  getDevMongoCreds(orgSlug: string, entityId?: string): Promise<DevMongoCreds>;
}

/** Response shape of `getDevMongoCreds` (BL449 Mongo half + M4 Redis half). */
export interface DevMongoCreds {
  /** True when the dev pool enforces auth and `url` carries credentials. */
  auth_required: boolean;
  /** A `mongodb://` template; may be credentialed and may contain `{app-name}`. */
  url: string;
  /** The per-org Mongo username (`org_<org-id>`), present when minted. */
  username?: string;
  /** True when THIS call minted the org user (first time), else false. */
  created?: boolean;
  /**
   * Dev Grid M4 — a per-org ACL-scoped credentialed dev `redis://` URL. Present
   * only post-cutover AND when the entity declares `requires: [redis]`; absent
   * otherwise (the runner then keeps the registry's no-auth dev REDIS_URL).
   */
  redis_url?: string;
}

/**
 * Errors thrown from {@link IV2Client.openTunnel}'s probe. The dev runner
 * matches on `kind` to render a §23-voice message that names the service
 * and (for network errors) the API URL.
 */
export class TunnelOpenError extends Error {
  constructor(
    public readonly kind: 'auth' | 'forbidden' | 'network',
    public readonly service: string,
    public readonly detail: string,
  ) {
    super(`Tunnel '${service}' failed (${kind}): ${detail}`);
    this.name = 'TunnelOpenError';
  }
}

/**
 * Default request→response budget for the deploy upload POSTs. The build is
 * async (202 + poll_url); this bounds only the upload leg so a stalled server
 * errors instead of hanging forever (the "getting stuck" bug). Generous by
 * default; override with `CLOUDGRID_PLUG_UPLOAD_TIMEOUT_MS`.
 */
const PLUG_UPLOAD_TIMEOUT_MS = Number(process.env.CLOUDGRID_PLUG_UPLOAD_TIMEOUT_MS) || 120_000;

export class V2Client implements IV2Client {
  private readonly baseUrl: string;
  private readonly jwt: string;
  private readonly orgSlug: string | null;
  private readonly yamlOrg: string | null;
  private readonly userAgent: string;
  private readonly uploadTimeoutMs: number;

  constructor(opts: V2ClientOpts) {
    this.baseUrl = opts.apiUrl.replace(/\/+$/, '');
    this.jwt = opts.jwt;
    this.orgSlug = opts.orgSlug;
    this.yamlOrg = opts.yamlOrg ?? null;
    this.userAgent = opts.userAgent ?? `cloudgrid-cli/${getCliVersion()} node/${process.version}`;
    this.uploadTimeoutMs = opts.uploadTimeoutMs ?? PLUG_UPLOAD_TIMEOUT_MS;
  }

  /** Returns the org slug this client is scoped to (null if no active org). */
  getOrgSlug(): string | null { return this.orgSlug; }

  /**
   * Backlog 320 — return a client scoped to `orgSlug`. Carries over jwt,
   * baseUrl, yamlOrg, and userAgent so the only thing that changes is the
   * `x-cloudgrid-org` header. Returns `this` when the slug is unchanged.
   */
  withOrgSlug(orgSlug: string | null): IV2Client {
    if (orgSlug === this.orgSlug) return this;
    return new V2Client({
      apiUrl: this.baseUrl,
      jwt: this.jwt,
      orgSlug,
      yamlOrg: this.yamlOrg,
      userAgent: this.userAgent,
      uploadTimeoutMs: this.uploadTimeoutMs,
    });
  }

  /** Returns the base API URL (without trailing slash). */
  getBaseUrl(): string { return this.baseUrl; }

  /**
   * Arm a per-request timeout. Returns an `AbortSignal` to hand to `fetch`, a
   * `didTimeout()` predicate (so a caught abort can be reclassified as a
   * timeout vs. an external cancellation), and two teardown functions:
   *
   *   - `clearTimer()` disarms only the timeout timer, leaving any external
   *     signal still wired to the controller. Streaming paths call this once
   *     the response headers arrive so a long-lived stream isn't killed
   *     mid-flight, while a caller's cancel signal still aborts it.
   *   - `dispose()` fully tears down (timer + external listener). Non-streaming
   *     paths call this in a `finally`.
   *
   * The window covers the whole request — headers AND body read — for the
   * non-streaming callers, since a server that sends headers then stalls on the
   * body is the same hang at a later point.
   */
  private startTimeout(external?: AbortSignal): {
    signal: AbortSignal;
    ms: number;
    didTimeout: () => boolean;
    clearTimer: () => void;
    dispose: () => void;
  } {
    const ms = resolveApiTimeoutMs();
    const controller = new AbortController();
    let timedOut = false;
    const onExternalAbort = () => controller.abort();
    if (external) {
      if (external.aborted) controller.abort();
      else external.addEventListener('abort', onExternalAbort, { once: true });
    }
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, ms);
    const clearTimer = () => clearTimeout(timer);
    return {
      signal: controller.signal,
      ms,
      didTimeout: () => timedOut,
      clearTimer,
      dispose: () => {
        clearTimer();
        if (external) external.removeEventListener('abort', onExternalAbort);
      },
    };
  }

  private buildHeaders(extra?: Record<string, string>): Record<string, string> {
    const h: Record<string, string> = {
      authorization: `Bearer ${this.jwt}`,
      'user-agent': this.userAgent,
      // Backlog 200 — every v2 request advertises the CLI version so the
      // API's min-cli-version middleware can reject builds below the
      // platform floor with a 426 + upgrade instructions.
      'x-cloudgrid-cli-version': getCliVersion(),
      ...extra,
    };
    // BL588 Phase E PR-E3 — emit both the legacy `x-cloudgrid-org` and the new
    // canonical `x-cloudgrid-grid` header (same value). The API accepts either
    // (Grid wins when both are present); Phase F drops the org name.
    if (this.orgSlug) {
      h['x-cloudgrid-org'] = this.orgSlug;
      h['x-cloudgrid-grid'] = this.orgSlug;
    }
    if (this.yamlOrg) h['x-cloudgrid-yaml-org'] = this.yamlOrg;
    return h;
  }

  async get<T>(path: string, qs?: Record<string, string | number | undefined>): Promise<T> {
    return this.request<T>('GET', this.withQuery(path, qs));
  }

  async post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', path, body);
  }

  async patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  async del<T>(path: string): Promise<T> {
    return this.request<T>('DELETE', path);
  }

  /**
   * Run an upload POST with a bounded request→response budget so a stalled
   * server surfaces a clear error instead of hanging forever (the "getting
   * stuck" bug). The build itself is async (202 + poll_url), so the timeout
   * covers only the upload leg. An `AbortError`/`TimeoutError` becomes a
   * `V2ApiError` whose message tells the user to check status before
   * re-deploying — never to blindly re-create, because the server may have
   * already created the entity.
   */
  private async uploadFetch(url: string, headers: Record<string, string>, form: FormData): Promise<Response> {
    try {
      return await fetch(url, {
        method: 'POST',
        headers,
        body: form,
        signal: AbortSignal.timeout(this.uploadTimeoutMs),
      });
    } catch (err) {
      const name = (err as { name?: string } | null)?.name;
      if (name === 'AbortError' || name === 'TimeoutError') {
        const seconds = Math.round(this.uploadTimeoutMs / 1000);
        throw new V2ApiError(
          0,
          'PLUG_UPLOAD_TIMEOUT',
          `Deploy request timed out after ${seconds}s — the build may still be running; ` +
            'run `grid status` before re-deploying to avoid a duplicate.',
        );
      }
      throw err;
    }
  }

  async postMultipart<T>(path: string, form: FormData): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    if (isVerbose()) console.error(`  [verbose] POST ${url} (multipart)`);
    // Deliberately NOT under the general request timeout: multipart uploads
    // keep #1842's uploadFetch (120s ceiling) — a large bundle on a slow
    // uplink needs more than the 15s request budget.
    const res = await this.uploadFetch(url, this.buildHeaders(), form);
    return this.parseResponse<T>(res);
  }

  async getOrgs(): Promise<Grid[]> {
    // Decision 045: call the `/grids` wire URL and read the `grid_id` wire
    // field. The API dual-emits `grid_id` alongside legacy `org_id` during the
    // transition, so `grid_id ?? org_id` reads correctly against both a new
    // (dual-emit) and an older (org_id-only) server.
    const resp = await this.get<{
      orgs?: Array<{ grid_id?: unknown; org_id?: unknown; slug?: unknown; name?: unknown }>;
    }>('/api/v2/grids');
    const list = Array.isArray(resp?.orgs) ? resp.orgs : [];
    const orgs: Grid[] = [];
    for (const o of list) {
      const id = typeof o?.grid_id === 'string' ? o.grid_id : o?.org_id;
      if (
        typeof id === 'string' &&
        typeof o?.slug === 'string' &&
        typeof o?.name === 'string'
      ) {
        orgs.push({ org_id: id, slug: o.slug, name: o.name });
      }
    }
    return orgs;
  }

  async plug(entityId: string, form: FormData, opts?: PlugOptions): Promise<PlugResult> {
    const url = `${this.baseUrl}/api/v2/entities/${entityId}/plug`;
    // Don't set Content-Type — fetch's multipart boundary belongs to the body.
    const extra = opts?.cliVersion ? { 'x-cloudgrid-cli-version': opts.cliVersion } : undefined;
    const headers = this.buildHeaders(extra);
    if (isVerbose()) console.error(`  [verbose] POST ${url} (multipart, plug)`);
    // Deliberately NOT under the general request timeout — see postMultipart:
    // the deploy upload keeps #1842's uploadFetch (120s ceiling).
    const res = await this.uploadFetch(url, headers, form);
    if (isVerbose()) console.error(`  [verbose] ${res.status} ${res.statusText}`);

    const text = await res.text();
    let body: unknown;
    try { body = text ? JSON.parse(text) : undefined; }
    catch { body = text; }

    if (res.status === 202) {
      const b = (body ?? {}) as Record<string, unknown>;
      const entity = (b.entity ?? {}) as Record<string, unknown>;
      const traceId = typeof b.trace_id === 'string' ? b.trace_id : '';
      return {
        kind: 'queued',
        trace_id: traceId,
        deploy_event_id: typeof b.deploy_event_id === 'string' ? b.deploy_event_id : '',
        entity_id: typeof b.entity_id === 'string' ? b.entity_id : entityId,
        entity: {
          id: typeof entity.id === 'string' ? entity.id : entityId,
          name: typeof entity.name === 'string' ? entity.name : '',
          namespace: typeof entity.namespace === 'string' ? entity.namespace : '',
        },
        poll_url: typeof b.poll_url === 'string' ? b.poll_url : `/deploys/${traceId}`,
        // BL567 — additive; only the queued path populates these.
        ...(typeof b.task_id === 'string' && b.task_id.length > 0 ? { task_id: b.task_id } : {}),
        ...(b.queued === true ? { queued: true } : {}),
        // BL659 — carry the server's canonical serving URL through so the
        // `--json outlet` (and human `Outlet:`) consumes it rather than
        // recomposing from the deploy trace's raw-primitive `result.url`.
        ...(typeof b.url === 'string' && b.url.length > 0 ? { url: b.url } : {}),
        ...readWarnings(b),
      };
    }

    if (res.status === 409) {
      const b = (body ?? {}) as Record<string, unknown>;
      return {
        kind: 'attached',
        trace_id: typeof b.trace_id === 'string' ? b.trace_id : null,
        status: typeof b.status === 'string' ? b.status : 'building',
        poll_url: typeof b.poll_url === 'string' ? b.poll_url : null,
        message: typeof b.message === 'string' ? b.message : undefined,
        // BL659 — same canonical serving URL on the attach envelope.
        ...(typeof b.url === 'string' && b.url.length > 0 ? { url: b.url } : {}),
        ...readWarnings(b),
      };
    }

    throw parseErrorBody(res.status, res.statusText, body, { requestId: readRequestId(res) });
  }

  async *streamSSE(
    path: string,
    qs?: Record<string, string | number | undefined>,
    signal?: AbortSignal,
  ): AsyncGenerator<import('../sse-parser.js').SSEEvent> {
    const { SSEParser } = await import('../sse-parser.js');
    const url = `${this.baseUrl}${this.withQuery(path, qs)}`;
    const headers = this.buildHeaders({ accept: 'text/event-stream' });
    // The timeout guards the CONNECTION (headers + any error-body read). Once a
    // stream is live it may legitimately run for minutes (`logs -f`), so we
    // disarm the timer before the read loop — the caller's `signal` stays wired
    // to the underlying fetch for mid-stream cancellation via startTimeout.
    const to = this.startTimeout(signal);
    try {
      const res = await fetch(url, { method: 'GET', headers, signal: to.signal });
      if (!res.ok) {
        const text = await res.text();
        let body: unknown;
        try { body = JSON.parse(text); } catch { body = text; }
        const { parseErrorBody } = await import('./v2-errors.js');
        throw parseErrorBody(res.status, res.statusText, body, { requestId: readRequestId(res) });
      }
      if (!res.body) throw new Error('No response body');
      to.clearTimer();
      const parser = new SSEParser();
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      const queue: import('../sse-parser.js').SSEEvent[] = [];
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        parser.feed(decoder.decode(value, { stream: true }), (ev) => queue.push(ev));
        while (queue.length > 0) yield queue.shift()!;
      }
      parser.feed('\n\n', (ev) => queue.push(ev));
      while (queue.length > 0) yield queue.shift()!;
    } catch (err) {
      if (to.didTimeout()) throw apiTimeoutError(to.ms);
      throw err;
    } finally {
      to.dispose();
    }
  }

  private withQuery(path: string, qs?: Record<string, string | number | undefined>): string {
    if (!qs) return path;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(qs)) {
      if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
    }
    const s = params.toString();
    return s ? `${path}?${s}` : path;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const headers = this.buildHeaders(body !== undefined ? { 'content-type': 'application/json' } : undefined);
    if (isVerbose()) console.error(`  [verbose] ${method} ${url}`);
    const to = this.startTimeout();
    try {
      const res = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: to.signal,
      });
      return await this.parseResponse<T>(res);
    } catch (err) {
      if (to.didTimeout()) throw apiTimeoutError(to.ms);
      throw err;
    } finally {
      to.dispose();
    }
  }

  private async parseResponse<T>(res: Response): Promise<T> {
    if (isVerbose()) console.error(`  [verbose] ${res.status} ${res.statusText}`);
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    let body: unknown;
    try { body = text ? JSON.parse(text) : undefined; }
    catch { body = text; }
    if (!res.ok) throw parseErrorBody(res.status, res.statusText, body, { requestId: readRequestId(res) });
    return body as T;
  }

  // ── WebSocket tunnel ─────────────────────────────────────────────────
  //
  // The relay (`/tunnel/<service>`) is the platform-owned replacement
  // for `kubectl port-forward` per decision 015. The probe-then-listen
  // shape (open one WS, wait for `open`, close it, then start the local
  // TCP server) is what surfaces 401 / 403 / network errors as a thrown
  // `TunnelOpenError` instead of as a silent failure on the first user
  // connection. The accepted-connection branch keeps the same byte-bridge
  // shape the legacy client used.

  /**
   * Build the WebSocket URL for `/tunnel/<service>` honouring the API protocol.
   * `entityId` rides as `?entity=<id>` so the API can scope the access check to
   * the entity's grid (CLI mega-plan Phase 1). The server requires it.
   */
  private buildTunnelUrl(service: string, entityId: string): string {
    const wsProto = this.baseUrl.startsWith('https') ? 'wss' : 'ws';
    const host = this.baseUrl.replace(/^https?:\/\//, '');
    return `${wsProto}://${host}/tunnel/${service}?entity=${encodeURIComponent(entityId)}`;
  }

  /**
   * Probe the relay once — open a WebSocket, await `open` or surface the
   * `unexpected-response` / `error`, close cleanly. Resolves on success,
   * rejects with `TunnelOpenError` whose `kind` discriminates the §23
   * wording the dev runner renders.
   */
  private probeTunnel(service: string, entityId: string): Promise<void> {
    const wsUrl = this.buildTunnelUrl(service, entityId);
    const headers = this.buildHeaders();
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl, { headers });
      let settled = false;
      const settle = (fn: () => void) => {
        if (settled) return;
        settled = true;
        fn();
      };

      ws.on('open', () => {
        settle(() => {
          try { ws.close(); } catch { /* ignore */ }
          resolve();
        });
      });

      // `unexpected-response` fires when the server rejects the upgrade
      // with an HTTP status (401 / 403 / 5xx). `IncomingMessage`'s
      // statusCode is what we discriminate on.
      ws.on('unexpected-response', (_req, res) => {
        const status = res.statusCode ?? 0;
        settle(() => {
          try { ws.close(); } catch { /* ignore */ }
          if (status === 401) {
            reject(new TunnelOpenError('auth', service, `HTTP ${status}`));
          } else if (status === 403) {
            reject(new TunnelOpenError('forbidden', service, `HTTP ${status}`));
          } else {
            reject(new TunnelOpenError('network', service, `HTTP ${status} on tunnel upgrade`));
          }
        });
      });

      ws.on('error', (err: Error) => {
        settle(() => {
          // If the upgrade-rejection handler already resolved/rejected,
          // this branch is suppressed by `settled`. Otherwise this is a
          // pre-upgrade transport failure (DNS, refused, TLS, ...).
          reject(new TunnelOpenError('network', service, err.message));
        });
      });
    });
  }

  async submitCliEvent(input: SubmitCliEventInput): Promise<void> {
    const url = `${this.baseUrl}/api/v2/telemetry/cli-events`;
    // CLI Telemetry v2 — session/trace ride as BODY fields (the v2 server reads
    // them from the body). We also keep the `x-cloudgrid-session` /
    // `x-cloudgrid-trace` request headers for continuity across proxies + logs.
    const extra: Record<string, string> = { 'content-type': 'application/json' };
    if (input.session_id) extra['x-cloudgrid-session'] = input.session_id;
    if (input.trace_id) extra['x-cloudgrid-trace'] = input.trace_id;
    const headers = this.buildHeaders(extra);
    const gridSlug = input.grid_slug ?? input.org_slug ?? this.orgSlug;
    const body: Record<string, unknown> = {
      event_type: input.event_type,
      command: input.command,
      cli_version: getCliVersion(),
      os: getOsName(),
      node_version: process.version,
      ts: new Date().toISOString(),
    };
    // identity — modernized (grid) with the legacy org_slug alias alongside so a
    // pre-v2 server still records grid attribution.
    if (gridSlug) {
      body.grid_slug = gridSlug;
      body.org_slug = gridSlug;
    }
    if (input.grid_id) body.grid_id = input.grid_id;
    if (input.entity_id) {
      body.entity_ref = input.entity_id;
      body.entity_id = input.entity_id; // legacy alias
    }
    // what
    if (input.args && Object.keys(input.args).length > 0) body.args = input.args;
    if (input.outcome) body.outcome = input.outcome;
    const durationMs = input.elapsed_ms;
    if (typeof durationMs === 'number') {
      body.duration_ms = durationMs;
      body.elapsed_ms = durationMs; // legacy alias
    }
    // intent
    if (input.entity_type) body.entity_type = input.entity_type;
    if (input.framework) body.framework = input.framework;
    if (input.description) body.description = input.description;
    // origin
    if (input.caller_kind) body.caller_kind = input.caller_kind;
    if (input.client_name) body.client_name = input.client_name;
    if (typeof input.headless === 'boolean') body.headless = input.headless;
    if (input.invocation_signals && Object.keys(input.invocation_signals).length > 0) {
      body.invocation_signals = input.invocation_signals;
    }
    // machine / env
    if (input.arch) body.arch = input.arch;
    if (input.os_version) body.os_version = input.os_version;
    if (input.cwd_hint) body.cwd_hint = input.cwd_hint;
    // session correlation (also on headers above)
    if (input.session_id) body.session_id = input.session_id;
    if (input.trace_id) body.trace_id = input.trace_id;
    // content
    if (input.error_code) body.error_code = input.error_code;
    if (typeof input.error_status === 'number') body.error_status = input.error_status;
    if (input.error_message) body.error_message = input.error_message;
    if (input.request_id) body.request_id = input.request_id;
    if (input.text) body.text = input.text;
    if (input.log_excerpt) body.log_excerpt = input.log_excerpt;
    if (input.metadata) body.metadata = input.metadata;

    // 250ms hard cap. The fetch is fire-and-forget from the CLI's
    // standpoint — we never want telemetry to extend a verb's wall-clock,
    // and on the error path the caller is about to `process.exit`.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 250);
    try {
      await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch {
      // Telemetry never errors visibly. Network failures, 4xx/5xx
      // responses, and aborts on timeout all swallow here.
    } finally {
      clearTimeout(timer);
    }
  }

  async getDevMongoCreds(orgSlug: string, entityId?: string): Promise<DevMongoCreds> {
    const path = `/api/v2/grids/${encodeURIComponent(orgSlug)}/dev-mongo-creds`;
    const body: Record<string, unknown> = {};
    if (entityId) body.entity_id = entityId;
    const resp = await this.post<Partial<DevMongoCreds>>(path, body);
    return {
      auth_required: resp?.auth_required === true,
      url: typeof resp?.url === 'string' ? resp.url : '',
      username: typeof resp?.username === 'string' ? resp.username : undefined,
      created: typeof resp?.created === 'boolean' ? resp.created : undefined,
      redis_url: typeof resp?.redis_url === 'string' ? resp.redis_url : undefined,
    };
  }

  async openTunnel(service: string, localPort: number, entityId: string): Promise<net.Server> {
    // Probe first — this surfaces auth + reachability errors as a thrown
    // TunnelOpenError before any local listener is opened, so the runner
    // can render §23 wording without leaving an open net.Server behind.
    await this.probeTunnel(service, entityId);

    return new Promise((resolve, reject) => {
      const server = net.createServer((tcpSocket) => {
        const wsUrl = this.buildTunnelUrl(service, entityId);
        const headers = this.buildHeaders();
        const ws = new WebSocket(wsUrl, { headers });
        ws.binaryType = 'nodebuffer';

        // Belt-and-suspenders client-side autoping. The API ships server-side
        // pings already (see `handleTunnel` in packages/api/src/services/tunnel.ts);
        // mirroring it here keeps the bridge healthy across more intermediary
        // configurations (NATs, corporate proxies, residential CGNATs) than a
        // one-sided ping reaches. 25s stays under typical 30s idle windows.
        let pingInterval: NodeJS.Timeout | undefined;

        ws.on('open', () => {
          pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              try { ws.ping(); } catch { /* close-in-flight; harmless */ }
            }
          }, 25_000);

          tcpSocket.on('data', (data) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(data);
          });
          ws.on('message', (data: Buffer) => {
            if (!tcpSocket.destroyed) tcpSocket.write(data);
          });
        });

        const stopPing = () => {
          if (pingInterval) {
            clearInterval(pingInterval);
            pingInterval = undefined;
          }
        };

        ws.on('close', () => {
          stopPing();
          if (!tcpSocket.destroyed) tcpSocket.destroy();
        });
        ws.on('error', () => {
          stopPing();
          if (!tcpSocket.destroyed) tcpSocket.destroy();
        });

        tcpSocket.on('close', () => {
          stopPing();
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        });
        tcpSocket.on('error', () => {
          stopPing();
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        });
      });

      server.on('error', reject);
      server.listen(localPort, '127.0.0.1', () => {
        server.removeListener('error', reject);
        // Subsequent listener errors (e.g., fd exhaustion mid-session)
        // are surfaced via stderr. The dev runner's per-service prefix
        // pipeline does not pipe through here — this is rare.
        server.on('error', (err) => {
          console.error(`Tunnel server error (${service}): ${err.message}`);
        });
        resolve(server);
      });
    });
  }
}

/**
 * Read the `X-Request-Id` header off a fetch Response. The header is
 * case-insensitive (Headers normalises lookups) but servers emit a mix
 * of `X-Request-Id` and `x-request-id` — `Headers.get` handles both.
 * Backlog 283: feeds the UNKNOWN-path classifier so telemetry preserves
 * the request_id even when the response body lacks a v2 envelope.
 */
function readRequestId(res: Response): string | undefined {
  const v = res.headers.get('x-request-id');
  return v && v.length > 0 ? v : undefined;
}

// ── Telemetry helpers (decision 016) ─────────────────────────────────────

function getOsName(): 'darwin' | 'linux' | 'win32' {
  switch (process.platform) {
    case 'darwin':
      return 'darwin';
    case 'win32':
      return 'win32';
    default:
      return 'linux';
  }
}

/**
 * Factory — reads credentials + active-org + config, produces a ready client.
 * Throws V2ApiError AUTH_REQUIRED if no JWT present. Commands should catch
 * and surface the canonical wording from `notSignedInMessage()` (spec v17
 * §15 names the env-var bypass; backlog 041.2 tracks the missing v0.8
 * login flow).
 */
export async function getV2Client(
  overrideOrgSlug?: string | null,
  yamlOrg?: string | null,
): Promise<V2Client> {
  const creds = readCredentials();
  if (!creds) throw new V2ApiError(401, 'AUTH_REQUIRED', notSignedInMessage());
  const cfg = loadConfig();
  const orgSlug = overrideOrgSlug !== undefined ? overrideOrgSlug : getActiveOrgSlug();
  return new V2Client({ apiUrl: cfg.api_url, jwt: creds.jwt, orgSlug, yamlOrg: yamlOrg ?? null });
}
